import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  ReachProSupportClient,
  ReachProSupportConversation,
} from 'src/WebApiController';

export const CONVERSATION_EVENTS_QUERY_KEY = 'reachpro-conversation-events';
export const CONVERSATION_CATALOG_EVENTS_QUERY_KEY =
  'reachpro-conversation-catalog-events';

export function useReachProConversationEvents(
  conversations: ReachProSupportConversation[] | null | undefined
) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);

  const eventIds = useMemo(() => {
    if (!conversations || conversations.length === 0) {
      return [];
    }
    // Get unique, non-null event IDs
    const ids = conversations
      .map((c) => c.eventId)
      .filter((id): id is number => id != null);
    return [...new Set(ids)];
  }, [conversations]);

  const shouldQuery =
    hasReachProSupportFeature &&
    activeAccountWebClientConfig.activeAccountId != null &&
    eventIds.length > 0;

  const { data: catalogEventsLookup, isLoading: isLoadingCatalogEvents } =
    useQuery({
      queryKey: [
        CONVERSATION_CATALOG_EVENTS_QUERY_KEY,
        activeAccountWebClientConfig.activeAccountId,
        JSON.stringify(eventIds),
      ],
      queryFn: async () => {
        if (!shouldQuery) {
          return null;
        }
        const result = await tryInvokeApi(
          async () => {
            return await new ReachProSupportClient(
              activeAccountWebClientConfig
            ).getCatalogEventsByIds(eventIds);
          },
          (error) => {
            trackError('ReachProSupportClient.getCatalogEventsByIds', error);
          }
        );

        return result;
      },
      enabled: shouldQuery,
      staleTime: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  return {
    catalogEventsLookup,
    isLoading: isLoadingCatalogEvents,
  };
}
